/**
 * TrustGrid Projects Page
 * Refactored to use TrustGridPage layout and ProjectList component
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Input, Button } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { createProject, listProjects, runScan, deleteProject } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { showError, showSuccess } from '../../../utils/notifications';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectControlsModal from './ProjectControlsModal';
import ProjectList from './ProjectList';
import LastScanFilterBar from './LastScanFilterBar';

const { TextArea } = Input;

function Projects() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectProject } = useTrustGrid();

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Projects', path: '/trustgrid/projects' }];
  }, [location.state]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [runningScans, setRunningScans] = useState({});
  const [controlsModalOpen, setControlsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Date filter state
  const [dateFilter, setDateFilter] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await listProjects();
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    try {
      await createProject({
        name: newProjectName,
        description: newProjectDesc
      });
      showSuccess('Project created successfully');
      setOpenDialog(false);
      setNewProjectName('');
      setNewProjectDesc('');
      fetchProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      showError(error);
    }
  };

  const handleRunScan = async (projectId) => {
    setRunningScans({ ...runningScans, [projectId]: true });
    try {
      await runScan(projectId);
      showSuccess('Scan started successfully');
      // Refresh projects after scan
      setTimeout(() => {
        fetchProjects();
        setRunningScans({ ...runningScans, [projectId]: false });
      }, 2000);
    } catch (error) {
      console.error('Failed to run scan:', error);
      showError(error);
      setRunningScans({ ...runningScans, [projectId]: false });
    }
  };

  const handleOpenDashboard = (projectId) => {
    // Find and select the project
    const project = projects.find(p => p.id === projectId);
    selectProject(projectId, project);
    navigate('/trustgrid/dashboard', {
      state: {
        from: '/trustgrid/projects',
        breadcrumbs: [
          { label: 'TrustGrid', path: '/trustgrid/dashboard' },
          { label: 'Projects', path: '/trustgrid/projects' },
        ]
      }
    });
  };

  const handleViewReports = (projectId) => {
    // Find and select the project
    const project = projects.find(p => p.id === projectId);
    selectProject(projectId, project);
    navigate('/trustgrid/reports', {
      state: {
        from: '/trustgrid/projects',
        breadcrumbs: [
          { label: 'TrustGrid', path: '/trustgrid/dashboard' },
          { label: 'Projects', path: '/trustgrid/projects' },
        ]
      }
    });
  };

  const handleManageControls = (project) => {
    setSelectedProject(project);
    setControlsModalOpen(true);
  };

  const handleControlsModalClose = () => {
    setControlsModalOpen(false);
    setSelectedProject(null);
  };

  const handleControlsSuccess = () => {
    // Optionally refresh projects after control assignment
    fetchProjects();
  };

  const handleDelete = (project) => {
    Modal.confirm({
      title: 'Delete Project',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to delete this project?</p>
          <p><strong>{project.name}</strong></p>
          <p style={{ color: '#ff4d4f', marginTop: '8px' }}>
            This will permanently delete all associated connectors, scans, and data. This action cannot be undone.
          </p>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteProject(project.id);
          showSuccess(`Project "${project.name}" deleted successfully`);
          fetchProjects();
        } catch (error) {
          console.error('Failed to delete project:', error);
          showError(error);
        }
      }
    });
  };

  /**
   * Filter projects by last scan date
   */
  const filteredProjects = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return projects;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return projects.filter((project) => {
      if (!project.updated_at) return false;
      const projectTime = new Date(project.updated_at).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return projectTime === filterTime;
        case 'after':
          return projectTime > filterTime;
        case 'before':
          return projectTime < filterTime;
        default:
          return true;
      }
    });
  }, [projects, dateFilter]);

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setDateFilter(null);
  };

  return (
    <TrustGridPage
      title="Projects"
      subtitle="Manage TrustGrid AI compliance projects"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchProjects}
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenDialog(true)}>
          New Project
        </Button>
      }
    >
      <ProjectList
        data={filteredProjects}
        loading={loading}
        onRefresh={fetchProjects}
        onOpenDashboard={handleOpenDashboard}
        onManageControls={handleManageControls}
        onRunScan={handleRunScan}
        onViewReports={handleViewReports}
        onDelete={handleDelete}
        runningScans={runningScans}
        externalFilterCount={dateFilter && dateFilter.date ? 1 : 0}
        onClearExternalFilters={handleClearFilters}
        toolbar={
          <LastScanFilterBar
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />
        }
      />

      {/* New Project Modal */}
      <Modal
        title="Create New Project"
        open={openDialog}
        onCancel={() => setOpenDialog(false)}
        onOk={handleCreateProject}
        okText="Create"
        okButtonProps={{ disabled: !newProjectName.trim() }}
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Project Name <span style={{ color: 'red' }}>*</span>
          </label>
          <Input
            autoFocus
            placeholder="Enter project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Description (optional)
          </label>
          <TextArea
            placeholder="Enter project description"
            rows={3}
            value={newProjectDesc}
            onChange={(e) => setNewProjectDesc(e.target.value)}
          />
        </div>
      </Modal>

      {/* Project Controls Modal */}
      <ProjectControlsModal
        open={controlsModalOpen}
        onClose={handleControlsModalClose}
        project={selectedProject}
        onSuccess={handleControlsSuccess}
      />
    </TrustGridPage>
  );
}

export default Projects;
